import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as d3 from 'd3';
import { IMultiCharts } from '../multi-charts/Imulti-charts';
import { PricingCurve } from '../../../../api';

@Component({
    selector: 'dynamica-step-line-chart',
    templateUrl: './step-line-chart.component.html',
    styleUrl: './step-line-chart.component.scss',
})
export class StepLineChartComponent implements OnChanges {
  @Input() data: PricingCurve[];
  @Input() chartConfig: IMultiCharts;
  @ViewChild('chartContainer', { static: true }) container: ElementRef;
  @ViewChild('chartParent', { static: true }) chartParent: ElementRef;

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.data && this.data.length > 0) {
      this.drawStepLine();
    } else {
      this.drawNoData();
    }
  }

  drawNoData() {
    const svg = d3.select(this.chartParent.nativeElement);
    svg.selectAll('svg').remove();
  }

  drawStepLine() {
    // Set up the dimensions and margins of the chart
    const margin = { top: 20, right: 40, bottom: 40, left: 40 };
    const width = this.chartConfig.width;
    const height = this.chartConfig.height - margin.top - margin.bottom;
    const innerWidth = width - margin.left - margin.right;

    // Create the SVG container and set the origin
    d3.select(this.chartParent.nativeElement).selectAll('svg').remove();
    const svg = d3
      .select(this.chartParent.nativeElement)
      .append('svg')
      .attr('width', width)
      .attr('height', this.chartConfig.height)
      .append('g')
      .attr('height', height)
      .attr('width', innerWidth)
      .attr('transform', `translate(${margin.left},${margin.top / 2})`);
    const maxPrice = Math.max(
      ...this.data.map((item) => Math.abs(item.priceDifference)),
    );

    // Set up the x and y scales
    const x = d3.scaleLinear().domain([800, 0]).range([0, innerWidth]);

    const y = d3.scaleLinear().domain([-maxPrice, maxPrice]).range([height, 0]);

    const xAxis = d3
      .axisBottom(x)
      .ticks(3)
      .tickValues([800, 0])
      .tickSizeInner(0)
      .tickSizeOuter(0)
      .tickFormat((d) => {
        return d + ' days';
      })
      .tickPadding(15);

    const yAxis = d3
      .axisLeft(y)
      .ticks(3)
      .tickValues([-maxPrice, 0, maxPrice])
      .tickSizeInner(-innerWidth)
      .tickSizeOuter(0)
      .tickFormat((d) => {
        return d.toFixed(0) + '%';
      })
      .tickPadding(5);

    svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis);

    // Add the y axis
    svg.append('g').attr('class', 'y-axis').attr('height', height).call(yAxis);

    // Define the step line function
    const step = d3
      .line()
      .x((d) => x(d.days))
      .y((d) => y(d.priceDifference))
      .curve(d3.curveStepBefore);

    svg
      .append('path')
      .datum(this.data) // Bind the data to the path
      .attr('class', 'step')
      .attr('d', step);

    // Tooltip
    const tooltip = d3
      .select(this.chartParent.nativeElement)
      .append('div')
      .attr('class', 'step-tooltip')
      .style('opacity', 0);

    const euroLocale = d3.formatLocale({
      decimal: '.',
      thousands: ',',
      grouping: [3],
      currency: ['£', ''],
    });
    const euroFormat = euroLocale.format('$,.0f');

    svg
      .selectAll('.step-point')
      .data(this.data)
      .enter()
      .append('circle')
      .attr('class', 'step-point')
      .attr('cx', (d) => x(d.days))
      .attr('cy', (d) => y(d.priceDifference))
      .attr('r', 4)
      .style('fill', '#AA75AA')
      .style('cursor', 'pointer')
      .on('mouseover', (event, d) => {
        tooltip.transition().duration(200).style('opacity', 1);
        tooltip.html(
          `Lead time: ${d.days}
          <br>
          Price: ${euroFormat(d.price)}
          <br>
          Change: ${d.priceDifference.toFixed(1)}%
          <br>
          Date: ${d3.timeFormat('%d/%m/%Y')(d.date)}`,
        );

        if (x(x.invert(event.offsetX - margin.left)) + 70 >= innerWidth) {
          tooltip.attr('style', null);
          tooltip
            .style('top', event.offsetY + 'px')
            .style(
              'right',
              innerWidth - x(x.invert(event.offsetX - margin.left)) + 40 + 'px',
            );
        } else {
          tooltip.attr('style', null);
          tooltip
            .style('top', event.offsetY + 'px')
            .style(
              'left',
              x(x.invert(event.offsetX - margin.left)) + 70 + 'px',
            );
        }
        //          .style('right', (innerWidth - (x(x.invert(event.offsetX - margin.left))) + 70) + 'px');
      })
      .on('mouseout', () =>
        tooltip.transition().duration(500).style('opacity', 0),
      );
  }
}
